import { useRef } from "react";
import { useTranslation } from "@/contexts/LanguageContext";
import {
  ChartBar,
  Printer,
  Warning,
  Lightbulb,
  Eye,
  CursorClick,
  WaveSine,
} from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { usePDFExport } from "@/utils/pdfExport";

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.05 } },
};
const itemAnim = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

// ---------------------------------------------------------------------------
// Histogram diagram — re-usable SVG. `bars` is an array of normalized heights
// (0..1) across 256 samples; we render them as 64 grouped bars for clarity.
// `mode` controls the color scheme (luminance vs RGB overlay).
// ---------------------------------------------------------------------------

function genBars(centers, widths, dark = true) {
  // centers/widths: arrays of (mean, sigma) for sums of gaussians.
  // We sample at 64 positions across the 0..1 tonal range.
  const N = 64;
  const out = new Array(N).fill(0);
  for (let i = 0; i < N; i++) {
    const x = i / (N - 1);
    let v = 0;
    for (let k = 0; k < centers.length; k++) {
      const c = centers[k];
      const w = widths[k];
      v += Math.exp(-0.5 * ((x - c) / w) ** 2);
    }
    out[i] = v;
  }
  // normalize
  const max = Math.max(...out, 0.0001);
  return out.map((v) => v / max);
}

function HistogramSVG({ bars, height = 120, mode = "luminance" }) {
  // bars = either an array of heights (luminance) OR {r,g,b} arrays for RGB.
  const W = 320;
  const H = height;
  const PAD_TOP = 6;
  const PAD_BTM = 18;
  const drawH = H - PAD_TOP - PAD_BTM;

  // Zone boundaries from left (0%) to right (100%) of the tonal range:
  // Black point | Shadows | Midtones | Highlights | White point
  const zones = [
    { from: 0.0, to: 0.02, fill: "#000000", label: "B" },
    { from: 0.02, to: 0.25, fill: "#1A1A1A", label: "S" },
    { from: 0.25, to: 0.75, fill: "#262626", label: "M" },
    { from: 0.75, to: 0.98, fill: "#1A1A1A", label: "H" },
    { from: 0.98, to: 1.0, fill: "#FFFFFF", label: "W" },
  ];

  const data = mode === "rgb" ? bars : { lum: bars };
  const N = (data.lum || data.r).length;
  const barW = W / N;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto block">
      {/* Zone background bands */}
      {zones.map((z, i) => (
        <rect
          key={i}
          x={z.from * W}
          y={PAD_TOP}
          width={(z.to - z.from) * W}
          height={drawH}
          fill={z.fill}
          opacity={z.label === "B" || z.label === "W" ? 0.9 : 0.5}
        />
      ))}

      {/* Vertical gridlines between zones */}
      {[0.02, 0.25, 0.5, 0.75, 0.98].map((x, i) => (
        <line
          key={i}
          x1={x * W}
          x2={x * W}
          y1={PAD_TOP}
          y2={PAD_TOP + drawH}
          stroke="#3F3F46"
          strokeWidth="0.5"
          strokeDasharray="2 2"
        />
      ))}

      {/* Bars */}
      {mode === "rgb" ? (
        <g>
          {/* Red channel */}
          {data.r.map((h, i) => (
            <rect
              key={`r-${i}`}
              x={i * barW}
              y={PAD_TOP + drawH * (1 - h)}
              width={barW * 0.95}
              height={drawH * h}
              fill="#EF4444"
              opacity={0.55}
            />
          ))}
          {/* Green channel */}
          {data.g.map((h, i) => (
            <rect
              key={`g-${i}`}
              x={i * barW}
              y={PAD_TOP + drawH * (1 - h)}
              width={barW * 0.95}
              height={drawH * h}
              fill="#22C55E"
              opacity={0.55}
            />
          ))}
          {/* Blue channel */}
          {data.b.map((h, i) => (
            <rect
              key={`b-${i}`}
              x={i * barW}
              y={PAD_TOP + drawH * (1 - h)}
              width={barW * 0.95}
              height={drawH * h}
              fill="#3B82F6"
              opacity={0.55}
            />
          ))}
        </g>
      ) : (
        <g>
          {data.lum.map((h, i) => (
            <rect
              key={i}
              x={i * barW}
              y={PAD_TOP + drawH * (1 - h)}
              width={barW * 0.95}
              height={drawH * h}
              fill="#F5A524"
            />
          ))}
        </g>
      )}

      {/* Baseline */}
      <line
        x1="0"
        x2={W}
        y1={PAD_TOP + drawH}
        y2={PAD_TOP + drawH}
        stroke="#71717A"
        strokeWidth="1"
      />

      {/* Zone labels */}
      <text x="2" y={H - 4} fontSize="9" fill="#71717A" fontFamily="monospace">0</text>
      <text x={W * 0.5 - 6} y={H - 4} fontSize="9" fill="#71717A" fontFamily="monospace">128</text>
      <text x={W - 16} y={H - 4} fontSize="9" fill="#71717A" fontFamily="monospace">255</text>
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Tonal-map visual: shows the 5 named zones across a gradient strip.
// ---------------------------------------------------------------------------

function TonalMap({ lang }) {
  const labelsEn = ["Black Point", "Shadows", "Midtones", "Highlights", "White Point"];
  const labelsEs = ["Punto Negro", "Sombras", "Medios Tonos", "Altas Luces", "Punto Blanco"];
  const labels = lang === "es" ? labelsEs : labelsEn;
  const zoneEdges = [0, 0.02, 0.25, 0.75, 0.98, 1.0];

  return (
    <div className="w-full">
      {/* Full grayscale gradient */}
      <div
        className="h-16 w-full rounded-md border border-white/10 mb-2"
        style={{
          background:
            "linear-gradient(to right, #000 0%, #000 2%, #2a2a2a 12%, #6e6e6e 35%, #b4b4b4 60%, #e8e8e8 85%, #fff 98%, #fff 100%)",
        }}
      />
      {/* Zone labels under */}
      <div className="relative h-12 w-full mt-1">
        {labels.map((label, i) => {
          const start = zoneEdges[i];
          const end = zoneEdges[i + 1];
          const left = `${start * 100}%`;
          const width = `${(end - start) * 100}%`;
          return (
            <div
              key={i}
              className="absolute top-0 text-center"
              style={{ left, width }}
            >
              <div className="h-2 border-t border-l border-r border-[#F5A524]/40 mx-auto" />
              <p className="text-[9px] sm:text-[10px] uppercase tracking-widest font-semibold text-[#F5A524] mt-1 leading-tight">
                {label}
              </p>
              <p className="text-[9px] text-[#71717A] font-mono">
                {Math.round(start * 255)}-{Math.round(end * 255)}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pre-computed histogram shapes for the 3 scene examples.
// ---------------------------------------------------------------------------

const SCENE_DATA = {
  low_key: {
    lum: genBars([0.08, 0.18], [0.06, 0.1]),
    r: genBars([0.1, 0.22], [0.05, 0.1]),
    g: genBars([0.08, 0.18], [0.06, 0.1]),
    b: genBars([0.07, 0.15], [0.06, 0.09]),
  },
  high_key: {
    lum: genBars([0.78, 0.92], [0.08, 0.05]),
    r: genBars([0.78, 0.92], [0.08, 0.05]),
    g: genBars([0.8, 0.92], [0.08, 0.05]),
    b: genBars([0.82, 0.92], [0.08, 0.05]),
  },
  high_contrast: {
    lum: genBars([0.04, 0.92], [0.05, 0.06]),
    r: genBars([0.04, 0.92], [0.05, 0.06]),
    g: genBars([0.04, 0.82], [0.05, 0.07]),
    b: genBars([0.04, 0.72], [0.05, 0.08]),
  },
};

// ---------------------------------------------------------------------------
// Page content (EN + ES)
// ---------------------------------------------------------------------------

const CONTENT = {
  en: {
    page_title: "The Histogram: Your Guide to Perfect Exposure",
    page_sub:
      "The histogram is an objective map of light. It works the same on Canon, Nikon, Fujifilm, and Sony — because it measures the sensor, not the brand. Learn to read it and you will never be fooled by a bright LCD screen again.",
    download: "Download as PDF",
    generating: "Generating...",

    intro_title: "What the histogram is",
    intro_body:
      "Every digital photo is made of millions of pixels, each with a brightness value from 0 (pure black) to 255 (pure white). The histogram simply counts how many pixels exist at each brightness level and stacks them up as a graph. The left edge represents pure black; the right edge represents pure white. Tall peaks mean 'lots of pixels at this brightness'; valleys mean 'few pixels.'",
    intro_body_2:
      "Your camera's LCD lies. It changes brightness based on ambient light, your battery level, and your screen settings. The histogram does not lie — it is a mathematical readout of the actual sensor data. Trust the graph, not your eyes.",

    tonal_title: "The Tonal Map — 5 zones from black to white",
    tonal_body:
      "Photographers divide the 0–255 tonal range into five named zones. Knowing where your subject sits on this map tells you whether your exposure preserved detail or threw it away.",
    tonal_zones: [
      {
        name: "Black Point",
        range: "0–5",
        body: "Pure black. No detail. The deepest possible value the sensor can record.",
      },
      {
        name: "Shadows",
        range: "5–64",
        body: "Dark areas of the scene with recoverable detail. Texture is visible but muted.",
      },
      {
        name: "Midtones",
        range: "64–192",
        body: "The heart of the image — skin tones, foliage, mid-gray asphalt all live here.",
      },
      {
        name: "Highlights",
        range: "192–250",
        body: "Bright areas with recoverable detail — bright clothing, lit walls, sky away from the sun.",
      },
      {
        name: "White Point",
        range: "250–255",
        body: "Pure white. No detail. The brightest possible value the sensor can record.",
      },
    ],

    clipping_title: "The Wall — clipping warning",
    clipping_alert: "DATA TOUCHING THE EDGE IS GONE FOREVER.",
    clipping_body:
      "When your histogram piles up against the left wall (value 0) or right wall (value 255), those pixels have been 'clipped.' The sensor recorded only 'pure black' or 'pure white' — no detail survived. Unlike midtone errors, clipping CANNOT be recovered in Lightroom, Photoshop, or any other software. The information is mathematically gone.",
    clipping_left: "Left-side clipping = crushed shadows (lost detail in dark areas)",
    clipping_right: "Right-side clipping = blown highlights (lost detail in bright areas)",
    clipping_rule:
      "Rule of thumb: if you must clip something, clip the shadows. Lost shadow detail looks like 'mood'; blown highlights look like a mistake.",

    no_perfect_title: "Important: there is NO 'perfect' bell-curve",
    no_perfect_body:
      "The most common student myth is that a 'good' histogram must look like a smooth bell-shaped mountain centered in the middle. This is wrong. A correctly exposed dark alley SHOULD be pushed to the left. A correctly exposed snowy mountain SHOULD be pushed to the right. The histogram must match the MOOD of the scene, not some abstract ideal. Your only job is to keep the data off the walls.",

    scenes_title: "Scene-based examples",
    scenes_intro:
      "Different scenes produce different histograms. Compare each photo to its histogram and notice that none of them look like a bell-curve — and all of them are correctly exposed for their intent.",
    scenes: [
      {
        key: "low_key",
        title: "Low Key — Dark Alley",
        body: "Most of the data is pushed to the LEFT — this is correct. The scene is dark by nature. Notice the histogram tapers smoothly off before reaching the left wall — no shadow clipping.",
        tip: "Tip: expose for the small bright accent (the lamp glow). Use spot metering if available.",
      },
      {
        key: "high_key",
        title: "High Key — Snowy Landscape",
        body: "Most of the data is pushed to the RIGHT — also correct. Snow is supposed to be bright. The histogram approaches but does not touch the right wall — highlight detail preserved.",
        tip: "Tip: in matrix/evaluative metering, dial in +1 to +1.7 EV exposure compensation to keep snow white instead of gray.",
      },
      {
        key: "high_contrast",
        title: "High Contrast — Sunset Silhouette",
        body: "Two tall peaks: one at the left (the dark silhouette) and one at the right (the bright sky). A deep valley in the middle. This is a high-dynamic-range scene — expose for the sky, let the foreground go black on purpose.",
        tip: "Tip: this is when you bracket exposures and consider HDR merging, or accept the silhouette as the creative choice.",
      },
    ],

    blinkies_title: "The Blinkies — your real-world companion",
    blinkies_body:
      "Almost every modern camera offers a 'Highlight Alert' (nicknamed 'the blinkies'). When enabled, any clipped highlight in your image preview will flash on the LCD — usually black or red blinking dots that pulse on the bright spots that lost detail.",
    blinkies_use: [
      "Use blinkies WITH the histogram, not instead of it. The histogram tells you HOW MUCH clipping; the blinkies tell you WHERE in the image.",
      "If the blinkies flash on someone's white shirt, you can recompose or lower exposure to save the detail.",
      "If the blinkies flash only on a small specular highlight (chrome, the sun itself, a lamp), let it clip — that is normal.",
    ],
    blinkies_enable_title: "How to enable the Blinkies (Highlight Alert):",
    blinkies_enable: [
      "Canon: Menu → Playback → Highlight Alert → Enable",
      "Nikon: Playback Menu → Playback Display Options → Highlights",
      "Sony: Menu → Playback → Display as Group → 'Highlight Display' (varies by model)",
      "Fujifilm: it appears automatically in playback if you enable 'Highlight Tone' display",
    ],

    hardware_title: "How to find your histogram — universal hardware guide",
    hardware_intro:
      "The Universal Toggle: nearly every modern DSLR and mirrorless camera uses the DISP or INFO button to cycle through screen overlays. Press it repeatedly during Live View OR during Playback until the histogram appears.",
    hardware_brands: [
      {
        name: "Canon",
        body: "Press INFO during Live View or Playback. Cycle through screens until you see the histogram (luminance + RGB on most newer bodies). Some models offer 'Quick Set' → 'Display Info' for a custom toggle.",
      },
      {
        name: "Nikon",
        body: "Press the DISP button (or the up/down arrows during Playback) to cycle to the histogram screen. Some bodies require enabling histogram display under Playback Menu → Playback Display Options.",
      },
      {
        name: "Sony",
        body: "Press the DISP button to cycle through display modes during shooting or playback. Sony mirrorless typically shows a small luminance histogram in the corner once enabled in Menu → Display.",
      },
      {
        name: "Fujifilm",
        body: "Press DISP/BACK to cycle screens. In playback, swipe up (touchscreen) or press up to see the full histogram + clipping warnings. Fuji's 'EVF Live View' can overlay it while composing.",
      },
    ],
    hardware_pro_tip:
      "Pro tip: most cameras hide the histogram by default. Spend 5 minutes once in your menu enabling 'show histogram on playback' AND 'show histogram during Live View / EVF'. You will use it on every single shot from then on.",

    modes_title: "Luminance vs. RGB — which view to use",
    modes_body:
      "Your camera offers two histogram styles. Learn both.",
    modes: [
      {
        name: "Luminance",
        body: "A single graph showing overall brightness (a weighted blend of R+G+B). Fast to read. Best for general exposure decisions and learning the basics.",
      },
      {
        name: "RGB",
        body: "Three overlaid graphs (red, green, blue). Slower but more accurate. Reveals when ONE channel is clipping while the others look fine — common with red flowers, sunset skies, and saturated fabrics. Pros always shoot with RGB enabled.",
      },
    ],

    closing_title: "Your daily practice",
    closing_body:
      "For one week: after every shot, glance at the histogram before reviewing the photo. You will be shocked how often the LCD looked 'fine' but the histogram revealed clipping you would have lost in editing. After a week, reading the histogram becomes automatic — you will trust the graph before you trust your eyes.",

    photo_caption: "Photo example",
    histogram_caption: "Histogram (luminance + RGB)",
    photo_missing: "Photo example coming soon.",
  },

  es: {
    page_title: "El Histograma: Tu Guia Para la Exposicion Perfecta",
    page_sub:
      "El histograma es un mapa objetivo de la luz. Funciona igual en Canon, Nikon, Fujifilm y Sony — porque mide el sensor, no la marca. Aprende a leerlo y nunca mas te engañara el brillo de la pantalla LCD.",
    download: "Descargar como PDF",
    generating: "Generando...",

    intro_title: "Que es el histograma",
    intro_body:
      "Cada foto digital esta hecha de millones de pixeles, cada uno con un valor de brillo de 0 (negro puro) a 255 (blanco puro). El histograma simplemente cuenta cuantos pixeles existen en cada nivel de brillo y los apila como una grafica. El borde izquierdo representa el negro puro; el derecho, el blanco puro. Los picos altos significan 'muchos pixeles en este brillo'; los valles significan 'pocos pixeles'.",
    intro_body_2:
      "La pantalla LCD de tu camara miente. Cambia de brillo segun la luz ambiente, el nivel de bateria y los ajustes de pantalla. El histograma NO miente — es una lectura matematica de los datos reales del sensor. Confia en la grafica, no en tus ojos.",

    tonal_title: "El Mapa Tonal — 5 zonas del negro al blanco",
    tonal_body:
      "Los fotografos dividen el rango tonal 0–255 en cinco zonas. Saber donde cae tu sujeto en este mapa te dice si tu exposicion conservo detalle o lo perdio.",
    tonal_zones: [
      {
        name: "Punto Negro",
        range: "0–5",
        body: "Negro puro. Sin detalle. El valor mas profundo que el sensor puede registrar.",
      },
      {
        name: "Sombras",
        range: "5–64",
        body: "Areas oscuras con detalle recuperable. La textura es visible pero apagada.",
      },
      {
        name: "Medios Tonos",
        range: "64–192",
        body: "El corazon de la imagen — tonos de piel, follaje, asfalto gris medio viven aqui.",
      },
      {
        name: "Altas Luces",
        range: "192–250",
        body: "Areas brillantes con detalle recuperable — ropa clara, muros iluminados, cielo lejos del sol.",
      },
      {
        name: "Punto Blanco",
        range: "250–255",
        body: "Blanco puro. Sin detalle. El valor mas brillante que el sensor puede registrar.",
      },
    ],

    clipping_title: "El Muro — alerta de recorte (clipping)",
    clipping_alert: "LOS DATOS QUE TOCAN EL BORDE SE PIERDEN PARA SIEMPRE.",
    clipping_body:
      "Cuando tu histograma se acumula contra el muro izquierdo (valor 0) o el derecho (valor 255), esos pixeles han sido 'recortados'. El sensor solo registro 'negro puro' o 'blanco puro' — no sobrevivio detalle. A diferencia de los errores de medios tonos, el clipping NO se puede recuperar en Lightroom, Photoshop ni ningun otro programa. La informacion se perdio matematicamente.",
    clipping_left: "Clipping izquierdo = sombras aplastadas (detalle perdido en zonas oscuras)",
    clipping_right: "Clipping derecho = altas luces quemadas (detalle perdido en zonas brillantes)",
    clipping_rule:
      "Regla practica: si tienes que recortar algo, recorta las sombras. El detalle perdido en sombras parece 'atmosfera'; las altas luces quemadas parecen un error.",

    no_perfect_title: "Importante: NO existe la 'curva perfecta' en forma de campana",
    no_perfect_body:
      "El mito mas comun del estudiante es que un histograma 'bueno' debe verse como una montaña suave centrada. ES FALSO. Un callejon oscuro bien expuesto DEBE estar empujado a la izquierda. Una montaña nevada bien expuesta DEBE estar empujada a la derecha. El histograma debe coincidir con el ESTADO de animo de la escena, no con un ideal abstracto. Tu unica tarea es mantener los datos fuera de los muros.",

    scenes_title: "Ejemplos por escena",
    scenes_intro:
      "Diferentes escenas producen diferentes histogramas. Compara cada foto con su histograma y nota que ninguno tiene forma de campana — y todos estan correctamente expuestos segun su intencion.",
    scenes: [
      {
        key: "low_key",
        title: "Low Key — Callejon Oscuro",
        body: "La mayoria de los datos esta empujada a la IZQUIERDA — esto es correcto. La escena es oscura por naturaleza. Observa que el histograma se afina suavemente antes de llegar al muro izquierdo — sin clipping de sombras.",
        tip: "Tip: expone para el pequeño acento brillante (el resplandor de la lampara). Usa medicion puntual si esta disponible.",
      },
      {
        key: "high_key",
        title: "High Key — Paisaje Nevado",
        body: "La mayoria de los datos esta empujada a la DERECHA — tambien correcto. La nieve debe verse brillante. El histograma se acerca pero no toca el muro derecho — detalle de altas luces preservado.",
        tip: "Tip: en medicion matricial/evaluativa, agrega +1 a +1.7 EV de compensacion de exposicion para que la nieve se vea blanca en vez de gris.",
      },
      {
        key: "high_contrast",
        title: "Alto Contraste — Silueta al Atardecer",
        body: "Dos picos altos: uno a la izquierda (la silueta oscura) y otro a la derecha (el cielo brillante). Un valle profundo en el centro. Es una escena de alto rango dinamico — expon para el cielo y deja que el primer plano se ennegrezca a proposito.",
        tip: "Tip: aqui es cuando haces bracketing y consideras fusion HDR, o aceptas la silueta como decision creativa.",
      },
    ],

    blinkies_title: "Los 'Blinkies' — tu compañero en el mundo real",
    blinkies_body:
      "Casi todas las camaras modernas ofrecen una 'Alerta de Altas Luces' (apodada 'los blinkies'). Al activarla, cualquier altura luz recortada en tu vista previa parpadeara en la LCD — normalmente puntos negros o rojos pulsando sobre las zonas que perdieron detalle.",
    blinkies_use: [
      "Usa los blinkies JUNTO con el histograma, no en su lugar. El histograma te dice CUANTO recorte hay; los blinkies te dicen DONDE en la imagen.",
      "Si los blinkies parpadean sobre la camisa blanca de alguien, puedes recomponer o bajar la exposicion para salvar el detalle.",
      "Si los blinkies solo parpadean en un brillo especular pequeño (cromo, el sol mismo, una lampara), dejalos recortar — eso es normal.",
    ],
    blinkies_enable_title: "Como activar los Blinkies (Alerta de Altas Luces):",
    blinkies_enable: [
      "Canon: Menu → Reproduccion → Aviso de altas luces → Activar",
      "Nikon: Menu de Reproduccion → Opciones de pantalla de reproduccion → Altas luces",
      "Sony: Menu → Reproduccion → 'Visualizacion de altas luces' (varia por modelo)",
      "Fujifilm: aparece automaticamente en reproduccion si activas la visualizacion 'Highlight Tone'",
    ],

    hardware_title: "Como encontrar tu histograma — guia universal de hardware",
    hardware_intro:
      "El Toggle Universal: casi toda DSLR y mirrorless moderna usa el boton DISP o INFO para alternar entre las pantallas de informacion. Pulsalo repetidamente en Live View O en Reproduccion hasta que aparezca el histograma.",
    hardware_brands: [
      {
        name: "Canon",
        body: "Pulsa INFO en Live View o Reproduccion. Recorre las pantallas hasta ver el histograma (luminancia + RGB en la mayoria de cuerpos nuevos). Algunos modelos ofrecen 'Quick Set' → 'Display Info' para un toggle personalizado.",
      },
      {
        name: "Nikon",
        body: "Pulsa el boton DISP (o las flechas arriba/abajo en Reproduccion) para llegar a la pantalla del histograma. Algunos cuerpos requieren activar la visualizacion en Menu de Reproduccion → Opciones de pantalla.",
      },
      {
        name: "Sony",
        body: "Pulsa el boton DISP para alternar modos de visualizacion al disparar o en reproduccion. Las mirrorless de Sony suelen mostrar un pequeño histograma de luminancia en la esquina una vez activado en Menu → Display.",
      },
      {
        name: "Fujifilm",
        body: "Pulsa DISP/BACK para cambiar pantallas. En reproduccion, desliza hacia arriba (pantalla tactil) o pulsa arriba para ver el histograma completo + alertas de clipping. La 'EVF Live View' de Fuji puede mostrarlo mientras compones.",
      },
    ],
    hardware_pro_tip:
      "Tip pro: la mayoria de las camaras esconden el histograma por defecto. Dedica 5 minutos una sola vez en el menu para activar 'mostrar histograma en reproduccion' Y 'mostrar histograma en Live View / EVF'. Lo usaras en cada disparo a partir de ese momento.",

    modes_title: "Luminancia vs. RGB — cual usar",
    modes_body: "Tu camara ofrece dos estilos de histograma. Aprende ambos.",
    modes: [
      {
        name: "Luminancia",
        body: "Una sola grafica que muestra el brillo general (una mezcla ponderada de R+G+B). Rapida de leer. Ideal para decisiones generales de exposicion y para aprender lo basico.",
      },
      {
        name: "RGB",
        body: "Tres graficas superpuestas (rojo, verde, azul). Mas lenta pero mas precisa. Revela cuando UN canal se esta recortando aunque los demas se vean bien — comun con flores rojas, cielos al atardecer y telas saturadas. Los profesionales siempre disparan con RGB activado.",
      },
    ],

    closing_title: "Tu practica diaria",
    closing_body:
      "Durante una semana: despues de cada disparo, mira el histograma antes de revisar la foto. Te sorprendera con que frecuencia la LCD se ve 'bien' pero el histograma revela un clipping que hubieras perdido al editar. Despues de una semana, leer el histograma se vuelve automatico — confiaras en la grafica antes que en tus ojos.",

    photo_caption: "Foto de ejemplo",
    histogram_caption: "Histograma (luminancia + RGB)",
    photo_missing: "Foto de ejemplo proxima.",
  },
};

const SCENE_PHOTOS = {
  low_key: "/histogram/low_key.png",
  high_key: "/histogram/high_key.png",
  high_contrast: "/histogram/high_contrast.png",
};

export default function HistogramPage() {
  const { lang } = useTranslation();
  const c = CONTENT[lang] || CONTENT.en;
  const printRef = useRef(null);

  const { ready, generating, download } = usePDFExport(
    printRef,
    () => `histogram-guide-${lang}.pdf`,
    [lang]
  );

  return (
    <div
      className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 print:bg-white print:text-black"
      data-testid="histogram-page"
    >
      {/* Screen */}
      <div className="print:hidden">
        <motion.div variants={container} initial="hidden" animate="show" className="space-y-8">
          {/* Header */}
          <motion.div variants={itemAnim} className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold text-white tracking-tight mb-2">
                {c.page_title}
              </h1>
              <p className="text-[#71717A] text-base max-w-3xl">{c.page_sub}</p>
            </div>
            <Button
              onClick={download}
              disabled={!ready}
              className="bg-[#F5A524] text-[#0A0A0A] hover:bg-[#F5B041] font-medium disabled:opacity-60"
              data-testid="histogram-download-btn"
            >
              <Printer className="w-4 h-4 mr-1.5" />
              {generating ? c.generating : c.download}
            </Button>
          </motion.div>

          {/* Intro */}
          <motion.div
            variants={itemAnim}
            className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6"
          >
            <div className="flex items-center gap-2 mb-2">
              <ChartBar weight="fill" className="w-5 h-5 text-[#F5A524]" />
              <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.intro_title}</h2>
            </div>
            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-3">{c.intro_body}</p>
            <p className="text-[#A1A1AA] text-sm leading-relaxed">{c.intro_body_2}</p>
          </motion.div>

          {/* Tonal Map */}
          <motion.div
            variants={itemAnim}
            className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6"
          >
            <div className="flex items-center gap-2 mb-2">
              <WaveSine weight="fill" className="w-5 h-5 text-[#F5A524]" />
              <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.tonal_title}</h2>
            </div>
            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-4">{c.tonal_body}</p>
            <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-4 mb-4">
              <TonalMap lang={lang} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
              {c.tonal_zones.map((z, i) => (
                <div
                  key={i}
                  className="bg-[#0A0A0A] border border-white/10 rounded-lg p-3"
                  data-testid={`tonal-zone-${i}`}
                >
                  <p className="text-[#F5A524] text-xs font-semibold uppercase tracking-widest mb-1">{z.name}</p>
                  <p className="text-[#71717A] text-[10px] font-mono mb-1">{z.range}</p>
                  <p className="text-[#A1A1AA] text-xs leading-relaxed">{z.body}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Clipping Alert */}
          <motion.div
            variants={itemAnim}
            className="bg-gradient-to-br from-rose-950/40 to-[#121214] border border-rose-500/40 rounded-xl p-6"
            data-testid="histogram-clipping-alert"
          >
            <div className="flex items-center gap-2 mb-3">
              <Warning weight="fill" className="w-5 h-5 text-rose-400" />
              <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.clipping_title}</h2>
            </div>
            <p className="text-rose-400 font-bold text-base mb-3 tracking-wide">{c.clipping_alert}</p>
            <p className="text-[#D4D4D8] text-sm leading-relaxed mb-3">{c.clipping_body}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
              <div className="bg-rose-950/30 border border-rose-500/20 rounded-lg p-3">
                <p className="text-[#D4D4D8] text-sm">{c.clipping_left}</p>
              </div>
              <div className="bg-rose-950/30 border border-rose-500/20 rounded-lg p-3">
                <p className="text-[#D4D4D8] text-sm">{c.clipping_right}</p>
              </div>
            </div>
            <div className="mt-4 bg-[#0A0A0A] border border-white/10 rounded-lg p-3">
              <p className="text-[#F5A524] text-xs uppercase font-semibold tracking-widest mb-1">
                Rule
              </p>
              <p className="text-[#A1A1AA] text-sm leading-relaxed">{c.clipping_rule}</p>
            </div>
          </motion.div>

          {/* No perfect bell-curve */}
          <motion.div
            variants={itemAnim}
            className="bg-[#121214]/80 backdrop-blur-md border border-[#F5A524]/40 rounded-xl p-6"
          >
            <div className="flex items-center gap-2 mb-2">
              <Lightbulb weight="fill" className="w-5 h-5 text-[#F5A524]" />
              <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.no_perfect_title}</h2>
            </div>
            <p className="text-[#A1A1AA] text-sm leading-relaxed">{c.no_perfect_body}</p>
          </motion.div>

          {/* Scenes */}
          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-2">
              <Eye weight="fill" className="w-5 h-5 text-[#F5A524]" />
              <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.scenes_title}</h2>
            </div>
            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-5">{c.scenes_intro}</p>

            <div className="space-y-6">
              {c.scenes.map((scene, i) => (
                <div
                  key={scene.key}
                  className="bg-[#0A0A0A] border border-white/10 rounded-xl overflow-hidden"
                  data-testid={`histogram-scene-${scene.key}`}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2">
                    <div className="aspect-[16/9] bg-black flex items-center justify-center overflow-hidden">
                      <img
                        src={SCENE_PHOTOS[scene.key]}
                        alt={scene.title}
                        className="w-full h-full object-cover"
                        crossOrigin="anonymous"
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.parentElement.innerHTML = `<div class='text-[#71717A] text-xs italic p-6 text-center'>${c.photo_missing}</div>`;
                        }}
                      />
                    </div>
                    <div className="p-4 bg-[#000000]/40 flex flex-col justify-center">
                      <p className="text-[#71717A] text-[10px] uppercase tracking-widest font-semibold mb-2">
                        {c.histogram_caption}
                      </p>
                      <HistogramSVG bars={SCENE_DATA[scene.key]} mode="rgb" height={140} />
                    </div>
                  </div>
                  <div className="p-5 border-t border-white/10">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-7 h-7 rounded-full bg-[#F5A524]/20 text-[#F5A524] flex items-center justify-center text-xs font-bold font-['JetBrains_Mono']">
                        {i + 1}
                      </span>
                      <h3 className="text-white font-medium text-base">{scene.title}</h3>
                    </div>
                    <p className="text-[#A1A1AA] text-sm leading-relaxed mb-2">{scene.body}</p>
                    <p className="text-[#F5A524] text-xs italic mt-2">{scene.tip}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Blinkies */}
          <motion.div
            variants={itemAnim}
            className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6"
          >
            <div className="flex items-center gap-2 mb-2">
              <CursorClick weight="fill" className="w-5 h-5 text-[#F5A524]" />
              <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.blinkies_title}</h2>
            </div>
            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-3">{c.blinkies_body}</p>
            <ul className="space-y-2 mb-4">
              {c.blinkies_use.map((u, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="mt-2 w-1 h-1 rounded-full bg-[#F5A524] shrink-0" />
                  <span className="text-[#A1A1AA] text-sm leading-relaxed">{u}</span>
                </li>
              ))}
            </ul>
            <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-4">
              <p className="text-[#F5A524] text-xs uppercase font-semibold tracking-widest mb-2">
                {c.blinkies_enable_title}
              </p>
              <ul className="space-y-1.5">
                {c.blinkies_enable.map((e, i) => (
                  <li key={i} className="text-[#D4D4D8] text-xs font-['JetBrains_Mono'] flex gap-2">
                    <span className="text-[#F5A524]">•</span> {e}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* Hardware Guide */}
          <motion.div
            variants={itemAnim}
            className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6"
          >
            <h2 className="font-['Outfit'] text-white text-xl font-medium mb-2">
              {c.hardware_title}
            </h2>
            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-4">{c.hardware_intro}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
              {c.hardware_brands.map((brand, i) => (
                <div
                  key={i}
                  className="bg-[#0A0A0A] border border-white/10 rounded-lg p-3"
                  data-testid={`histogram-hw-${brand.name.toLowerCase()}`}
                >
                  <p className="text-[#F5A524] font-medium text-sm mb-1.5">{brand.name}</p>
                  <p className="text-[#A1A1AA] text-xs leading-relaxed">{brand.body}</p>
                </div>
              ))}
            </div>
            <div className="bg-[#F5A524]/10 border border-[#F5A524]/30 rounded-lg p-3">
              <p className="text-[#F5A524] text-sm leading-relaxed">{c.hardware_pro_tip}</p>
            </div>
          </motion.div>

          {/* Luminance vs RGB */}
          <motion.div
            variants={itemAnim}
            className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6"
          >
            <h2 className="font-['Outfit'] text-white text-xl font-medium mb-2">
              {c.modes_title}
            </h2>
            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-4">{c.modes_body}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-4">
                <p className="text-[#F5A524] font-medium text-sm mb-2">{c.modes[0].name}</p>
                <div className="mb-3 bg-black rounded p-2 border border-white/5">
                  <HistogramSVG bars={genBars([0.25, 0.55, 0.78], [0.08, 0.1, 0.06])} mode="luminance" height={90} />
                </div>
                <p className="text-[#A1A1AA] text-xs leading-relaxed">{c.modes[0].body}</p>
              </div>
              <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-4">
                <p className="text-[#F5A524] font-medium text-sm mb-2">{c.modes[1].name}</p>
                <div className="mb-3 bg-black rounded p-2 border border-white/5">
                  <HistogramSVG
                    bars={{
                      r: genBars([0.3, 0.62, 0.82], [0.08, 0.1, 0.06]),
                      g: genBars([0.25, 0.55, 0.78], [0.08, 0.1, 0.06]),
                      b: genBars([0.2, 0.5, 0.7], [0.08, 0.1, 0.06]),
                    }}
                    mode="rgb"
                    height={90}
                  />
                </div>
                <p className="text-[#A1A1AA] text-xs leading-relaxed">{c.modes[1].body}</p>
              </div>
            </div>
          </motion.div>

          {/* Closing */}
          <motion.div
            variants={itemAnim}
            className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6"
          >
            <h2 className="font-['Outfit'] text-white text-xl font-medium mb-2">
              {c.closing_title}
            </h2>
            <p className="text-[#A1A1AA] text-sm leading-relaxed italic">"{c.closing_body}"</p>
          </motion.div>
        </motion.div>
      </div>

      {/* Print / PDF */}
      <div ref={printRef} className="hidden print:block bg-white text-black p-4" data-testid="histogram-print-view">
        <h1 className="text-3xl font-bold text-center mb-1">{c.page_title}</h1>
        <p className="text-center text-gray-500 mb-5 text-sm">{c.page_sub}</p>

        <div className="mb-5 break-inside-avoid">
          <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">1. {c.intro_title}</h2>
          <p className="text-sm text-gray-700 mb-2">{c.intro_body}</p>
          <p className="text-sm text-gray-700">{c.intro_body_2}</p>
        </div>

        <div className="mb-5 break-inside-avoid">
          <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">2. {c.tonal_title}</h2>
          <p className="text-sm text-gray-700 mb-3">{c.tonal_body}</p>
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 p-2 text-left">Zone</th>
                <th className="border border-gray-300 p-2 text-left">0-255 Range</th>
                <th className="border border-gray-300 p-2 text-left">Description</th>
              </tr>
            </thead>
            <tbody>
              {c.tonal_zones.map((z, i) => (
                <tr key={i}>
                  <td className="border border-gray-300 p-2 font-semibold">{z.name}</td>
                  <td className="border border-gray-300 p-2 font-mono">{z.range}</td>
                  <td className="border border-gray-300 p-2">{z.body}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mb-5 break-inside-avoid bg-rose-50 border-l-4 border-rose-500 p-3">
          <h2 className="text-xl font-bold mb-2 text-rose-700">⚠ 3. {c.clipping_title}</h2>
          <p className="font-bold text-rose-700 mb-2 text-sm">{c.clipping_alert}</p>
          <p className="text-sm text-gray-800 mb-2">{c.clipping_body}</p>
          <p className="text-xs text-gray-700">• {c.clipping_left}</p>
          <p className="text-xs text-gray-700">• {c.clipping_right}</p>
          <p className="text-xs text-gray-800 italic mt-2"><strong>Rule:</strong> {c.clipping_rule}</p>
        </div>

        <div className="mb-5 break-inside-avoid">
          <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">4. {c.no_perfect_title}</h2>
          <p className="text-sm text-gray-700">{c.no_perfect_body}</p>
        </div>

        <div className="mb-5 break-inside-avoid">
          <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">5. {c.scenes_title}</h2>
          <p className="text-sm text-gray-700 mb-3">{c.scenes_intro}</p>
          {c.scenes.map((s, i) => (
            <div key={s.key} className="mb-3 pb-3 border-b border-gray-200">
              <p className="font-bold text-sm">{i + 1}. {s.title}</p>
              <p className="text-xs text-gray-700 mt-1">{s.body}</p>
              <p className="text-xs text-gray-600 italic mt-1">{s.tip}</p>
            </div>
          ))}
        </div>

        <div className="mb-5 break-inside-avoid">
          <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">6. {c.blinkies_title}</h2>
          <p className="text-sm text-gray-700 mb-2">{c.blinkies_body}</p>
          <ul className="list-disc ml-5 space-y-1 mb-2">
            {c.blinkies_use.map((u, i) => (
              <li key={i} className="text-xs text-gray-700">{u}</li>
            ))}
          </ul>
          <p className="text-xs font-bold mt-2">{c.blinkies_enable_title}</p>
          <ul className="list-disc ml-5 space-y-1">
            {c.blinkies_enable.map((e, i) => (
              <li key={i} className="text-xs text-gray-700 font-mono">{e}</li>
            ))}
          </ul>
        </div>

        <div className="mb-5 break-inside-avoid">
          <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">7. {c.hardware_title}</h2>
          <p className="text-sm text-gray-700 mb-2">{c.hardware_intro}</p>
          <table className="w-full text-xs border-collapse mb-2">
            <tbody>
              {c.hardware_brands.map((b, i) => (
                <tr key={i}>
                  <td className="border border-gray-300 p-2 font-semibold w-1/5">{b.name}</td>
                  <td className="border border-gray-300 p-2">{b.body}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-gray-800 italic"><strong>Pro tip:</strong> {c.hardware_pro_tip}</p>
        </div>

        <div className="mb-5 break-inside-avoid">
          <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">8. {c.modes_title}</h2>
          <p className="text-sm text-gray-700 mb-2">{c.modes_body}</p>
          {c.modes.map((m, i) => (
            <div key={i} className="mb-2">
              <p className="text-sm font-bold">{m.name}</p>
              <p className="text-xs text-gray-700">{m.body}</p>
            </div>
          ))}
        </div>

        <p className="text-sm text-gray-600 italic text-center max-w-2xl mx-auto leading-relaxed mt-8">
          "{c.closing_body}"
        </p>
        <p className="text-center text-xs text-gray-400 mt-4">www.dslradvisor.com</p>
      </div>
    </div>
  );
}
